import { useEffect, useRef, useState } from 'react'

/** How long to keep the loading overlay up after a connected call ends. */
export const CALL_SWITCHING_LOADER_DURATION_MS = 2000

/**
 * When an active call ends, the footer control swaps from "Hang up" to "Call again" in the
 * exact same position. If the patient hangs up at the same moment the provider reaches to hang
 * up, the provider can land on "Call again" and accidentally re-dial the patient (QMD-3703).
 *
 * To guard the swap, this returns `isSwitchingCall === true` for a couple seconds whenever a
 * connected call ends. Callers show the existing loading overlay during that window, which sits
 * above the controls and absorbs the click — no button needs to be disabled.
 */
export function useCallSwitchingLoader(isCallActive: boolean) {
  const [isSwitchingCall, setIsSwitchingCall] = useState(false)
  const wasCallActiveRef = useRef(isCallActive)

  useEffect(() => {
    const callJustEnded = wasCallActiveRef.current && !isCallActive
    wasCallActiveRef.current = isCallActive

    if (!callJustEnded) return

    setIsSwitchingCall(true)
    const timeout = setTimeout(() => setIsSwitchingCall(false), CALL_SWITCHING_LOADER_DURATION_MS)
    return () => clearTimeout(timeout)
  }, [isCallActive])

  return { isSwitchingCall }
}
